import { Image } from "lucide-react";
import {
  Create,
  Datagrid,
  Edit,
  List,
  SimpleForm,
  TextField,
  TextInput,
  SelectInput,
  ImageInput,
  ImageField,
  required,
  useNotify,
  useRedirect,
  useRefresh,
  DateField,
  useRecordContext,
} from "react-admin";
import { useWatch } from "react-hook-form";
import { CardListActions } from "../../components";

const PageDescription = () => (
  <div className="page-description">
    <h1>Ad Assets</h1>
    <p>
      Manage ad assets (images, videos, HTML content) that can be used in ad
      placements across the site.
    </p>
    <p>
      Click and impression stats live on Ad Placements, which filters by
      location and speaker.
    </p>
  </div>
);

const AddEditPageDescription = () => (
  <div className="page-description">
    <h1>Add or Edit Ad Asset</h1>
    <p>
      Create or modify an ad asset. Assets can be images (URL), videos (URL), or
      custom HTML content.
    </p>
  </div>
);

const typeChoices = [
  { id: "image", name: "Image" },
  { id: "video", name: "Video" },
  { id: "html", name: "HTML" },
];

const filters = [
  <TextInput source="id" label="ID" key="id" alwaysOn />,
  <TextInput source="title" label="Title" key="title" alwaysOn />,
  <SelectInput
    source="type"
    label="Type"
    choices={typeChoices}
    key="type"
    alwaysOn
  />,
];

// Preview component for content (used in ListView)
const ContentPreview = () => {
  const record = useRecordContext();
  if (!record) return null;

  if (record.type === "image") {
    return (
      <img
        src={record.content_default}
        alt="Preview"
        style={{ maxWidth: 100, maxHeight: 60 }}
      />
    );
  }
  if (record.type === "video") {
    return (
      <span style={{ color: "#666" }}>
        🎬 {record.content_default?.substring(0, 30)}...
      </span>
    );
  }
  if (record.type === "html") {
    return (
      <span style={{ color: "#666" }}>
        📄 HTML ({record.content_default?.length || 0} chars)
      </span>
    );
  }
  return null;
};

// Preview component for EditView (shows current asset preview at top of form)
const CurrentAssetPreview = () => {
  const record = useRecordContext();
  if (!record) return null;

  let preview = null;
  if (record.type === "image" && record.content_default) {
    preview = (
      <img
        src={record.content_default}
        alt={record.title || "Asset preview"}
        style={{ maxWidth: 300, maxHeight: 200, objectFit: "contain" }}
      />
    );
  } else if (record.type === "video" && record.content_default) {
    preview = (
      <video
        src={record.content_default}
        style={{ maxWidth: 300, maxHeight: 200 }}
        controls
        muted
      />
    );
  } else if (record.type === "html" && record.content_default) {
    preview = (
      <div
        style={{
          maxWidth: 300,
          maxHeight: 200,
          overflow: "auto",
          border: "1px solid #ccc",
          padding: 8,
          background: "#f9f9f9",
        }}
        dangerouslySetInnerHTML={{ __html: record.content_default }}
      />
    );
  }

  if (!preview) return null;

  return (
    <div style={{ marginBottom: 16 }}>
      <h4 style={{ marginBottom: 8 }}>Current Asset Preview</h4>
      {preview}
    </div>
  );
};

// Conditionally show image uploaders when type is "image"
const ImageUploadFields = () => {
  const type = useWatch({ name: "type" });

  if (type !== "image") return null;

  return (
    <div style={{ marginTop: 16, padding: 16, background: "#f5f5f5", borderRadius: 4 }}>
      <h4 style={{ marginTop: 0, marginBottom: 8 }}>Or Upload Images</h4>
      <p style={{ fontSize: 13, color: "#666", marginBottom: 16 }}>
        Uploading an image will override the URL entered above.
      </p>
      <ImageInput
        source="FILE_content_default"
        label="Upload Default Image"
        accept="image/*"
        placeholder={<p>Drop an image here or click to select (takes precedence over URL)</p>}
      >
        <ImageField source="src" title="title" />
      </ImageInput>
      <ImageInput
        source="FILE_content_mobile"
        label="Upload Mobile Image (Optional)"
        accept="image/*"
        placeholder={<p>Drop a mobile image here or click to select</p>}
      >
        <ImageField source="src" title="title" />
      </ImageInput>
    </div>
  );
};

function ListView(props) {
  return (
    <>
      <PageDescription />
      <List
        {...props}
        actions={<CardListActions exporter={true} />}
        filters={filters}
        perPage={50}
        sort={{ field: "created_at", order: "DESC" }}
        title="Ad Assets"
      >
        <Datagrid rowClick="edit">
          <TextField source="id" label="ID" />
          <TextField source="title" label="Title" />
          <TextField source="type" label="Type" />
          <ContentPreview label="Preview" />
          <DateField source="created_at" label="Created" showTime />
        </Datagrid>
      </List>
    </>
  );
}

const AdAssetForm = ({ redirect, isEdit = false }) => {
  return (
    <SimpleForm redirect={redirect}>
      <AddEditPageDescription />
      {isEdit && <CurrentAssetPreview />}
      <SelectInput
        source="type"
        label="Asset Type"
        choices={typeChoices}
        validate={[required()]}
      />
      <TextInput
        source="title"
        label="Title"
        fullWidth
        helperText="Human-readable name for the asset. Used as image alt text for accessibility."
      />
      <TextInput
        source="content_default"
        label="Default Content (URL or HTML)"
        fullWidth
        multiline
        helperText="For images/videos: enter the URL. For HTML: enter the full HTML content. Will be overridden if you upload an image below."
      />
      <TextInput
        source="content_mobile"
        label="Mobile Content (Optional)"
        fullWidth
        multiline
        helperText="Optional mobile-specific content. If empty, default content will be used."
      />
      <ImageUploadFields />
    </SimpleForm>
  );
};

function EditView(props) {
  const notify = useNotify();
  const refresh = useRefresh();
  const redirect = useRedirect();
  return (
    <Edit
      {...props}
      mutationMode="pessimistic"
      mutationOptions={{
        onSuccess: () => {
          notify("Ad asset updated successfully!", { type: "success" });
          refresh();
          redirect(false);
        },
        onError: (error) => {
          notify(`Error: ${error.message}`, { type: "error" });
        },
      }}
    >
      <AdAssetForm redirect={false} isEdit={true} />
    </Edit>
  );
}

function CreateView(props) {
  const notify = useNotify();
  const refresh = useRefresh();
  const redirect = useRedirect();
  return (
    <Create
      {...props}
      mutationMode="pessimistic"
      mutationOptions={{
        onSuccess: () => {
          notify("Ad asset created successfully!", { type: "success" });
          refresh();
          redirect("list", "ad_assets");
        },
        onError: (error) => {
          notify(`Error: ${error.message}`, { type: "error" });
        },
      }}
    >
      <AdAssetForm redirect="list" />
    </Create>
  );
}

export const AdAssets = {
  category: "ads",
  name: "ad_assets",
  icon: Image,
  list: ListView,
  edit: EditView,
  create: CreateView,
  options: { label: "Ad Assets" },
};
